import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook for fetching API data with loading, error, and data states
 * @param {Function} apiCall - The API function to call
 * @param {Array} dependencies - Dependencies array for useEffect
 * @param {Object} options - Additional options
 * @param {boolean} options.immediate - Whether to fetch immediately (default: true)
 * @param {*} options.fallback - Fallback data to use on error (default: null)
 * @param {boolean} options.unwrapStrapi - Whether to unwrap Strapi nested responses (default: true)
 * @param {Function} options.transform - Optional function to transform the data before setting state
 * @param {string|Function} options.errorMessage - Custom error message or function that receives error
 * @param {string} options.resourceName - Optional resource name for better error messages (e.g., "menu", "footer")
 * @param {boolean} options.silentErrors - Suppress console errors for expected failures like 404s (default: false)
 * @returns {Object} { data, loading, error, setData, refetch }
 */
const useApiData = (apiCall, dependencies = [], options = {}) => {
  const {
    immediate = true,
    fallback = null,
    unwrapStrapi = true,
    transform = null,
    errorMessage = null,
    resourceName = "content",
    silentErrors = false,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCall();

      // Check if response is a Response object (has .ok property) or already processed data
      let result;
      if (response && typeof response === "object" && "ok" in response) {
        // It's a Response object, process it
        if (!response.ok) {
          const error = new Error(`HTTP ${response.status}`);
          error.status = response.status;
          error.response = response;
          throw error;
        }
        const json = await response.json();

        // Unwrap Strapi nested responses if enabled
        if (unwrapStrapi) {
          // Handle Strapi double nesting: { data: { data: {...}, meta: {} } }
          result = json.data?.data || json.data || json || null;
        } else {
          result = json.data || json || null;
        }
      } else {
        // It's already processed data, use it directly
        result = response;
      }

      // Apply transform function if provided
      if (transform && typeof transform === "function") {
        result = transform(result);
      }

      setData(result);

      return result;
    } catch (err) {
      // Determine error type and create friendly message
      let friendlyMessage;
      const status = err.status || err.response?.status;

      if (errorMessage) {
        // Use custom error message if provided
        friendlyMessage = typeof errorMessage === "function" ? errorMessage(err) : errorMessage;
      } else {
        // Generate friendly error message based on status
        switch (status) {
          case 404:
            friendlyMessage = `${resourceName.charAt(0).toUpperCase() + resourceName.slice(1)} not found`;
            break;
          case 500:
          case 502:
          case 503:
            friendlyMessage = `Server error while loading ${resourceName}`;
            break;
          case 401:
            friendlyMessage = "Authentication required";
            break;
          case 403:
            friendlyMessage = "Access denied";
            break;
          default:
            if (err.message?.includes("fetch")) {
              friendlyMessage = `Network error while loading ${resourceName}`;
            } else {
              friendlyMessage = `Unable to load ${resourceName}`;
            }
        }
      }

      // Log error to console unless silentErrors is true or it's an expected 404 with fallback
      const isExpected404WithFallback = status === 404 && fallback !== null;
      if (!silentErrors && !isExpected404WithFallback) {
        console.log(`Error loading ${resourceName}:`, err);
      }

      setError(friendlyMessage);

      // Set fallback data if provided
      if (fallback !== null) {
        setData(fallback);
      } else {
        setData(null);
      }

      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, unwrapStrapi, transform, errorMessage, fallback, resourceName, silentErrors]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    data,
    loading,
    error,
    setData,
    refetch: fetchData,
  };
};

export default useApiData;
